import { useContext, useEffect, useState } from "react";
import { UserContext } from "../../context/UserContext";
import apiFetch from "../../context/api";

const useUserTokenPrivileges = () => {
  const [token] = useContext(UserContext);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // If there’s no token, skip fetch and flip off loading
    if (!token) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    const getUser = async () => {
      setIsLoading(true);
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      let response;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          response = await apiFetch("/v1/users/me", { headers });
          if (response.status !== 502 && response.status !== 503 && response.status !== 504) break;
          if (attempt < 2) await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
        } catch (err) {
          if (attempt < 2) { await new Promise(r => setTimeout(r, 1000 * (attempt + 1))); continue; }
          console.error("Error in getUser:", err);
          setUser(null);
          setIsLoading(false);
          return;
        }
      }

      try {
        if (!response.ok) {
          if (response.status === 401 || response.status === 403 || response.status === 404) {
            console.warn(`[useUserTokenPrivileges] Invalid token or user not found (status: ${response.status})`);
            setUser(null);
            if (response.status === 404) {
              localStorage.removeItem("kongToken");
            }
          } else {
            console.warn("[useUserTokenPrivileges] Server error:", response.status, "— keeping session");
          }
        } else {
          const data = await response.json();
          setUser(data);
        }
      } catch (err) {
        console.error("Error in getUser:", err);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    getUser();
  }, [token]);

  return {
    token,
    user,
    accessPrivileges: user?.access_privileges ?? null,
    isLoading,
  };
};

export default useUserTokenPrivileges;
